import React, { useEffect, useState } from 'react';
import { useI18n } from '../context/I18nContext';

/**
 * Custom floating chat button that replaces the default LiveChatInc (LC)
 * minimized widget. The LC widget is rendered inside an iframe served from
 * `secure.livechatinc.com`, so its icon + surrounding white plate cannot be
 * styled from our app. Workaround:
 *
 *   1. Inject CSS that hides the stock `#chat-widget-minimized` iframe.
 *   2. Render OUR OWN bottom-right FAB — Intercom-style chat bubble mark
 *      (rounded-square bubble with tail) re-coloured to the ZHELTA amber
 *      palette. Hand-traced SVG (no trademark issues) so it's crisp at
 *      any DPR and has no white background to mask.
 *   3. Clicking the custom FAB calls `window.LiveChatWidget.call('maximize')`
 *      which is LC's public JS API for opening the expanded chat window.
 */

export default function ChatButtonOverride() {
  const [ready, setReady] = useState(false);
  const { lang } = useI18n() || {};

  // 1. Inject the "hide default LiveChat FAB" style ONCE, and keep a handle
  //    so we can re-append it in case LiveChat re-renders the iframe.
  useEffect(() => {
    const STYLE_ID = 'zh-lc-hide-default';
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    // LC's minimized widget is `iframe#chat-widget-minimized`. We hide it
    // visually but keep it clickable-sized=0 so clicks don't land on it.
    // The maximized window `#chat-widget` is NOT hidden — users still see
    // the chat pop-up when our button opens it.
    style.textContent = `
      iframe#chat-widget-minimized {
        opacity: 0 !important;
        pointer-events: none !important;
        width: 0 !important;
        height: 0 !important;
      }
    `;
    document.head.appendChild(style);
  }, []);

  // 2. Poll for the LC widget ready signal. `window.LiveChatWidget` comes
  //    from LC's loader script (present via emergent-main.js). Give up
  //    after ~10s — our button simply won't render, which is safer than
  //    showing a non-functional FAB.
  useEffect(() => {
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      if (typeof window.LiveChatWidget !== 'undefined' || document.getElementById('chat-widget-minimized')) {
        setReady(true);
        clearInterval(timer);
      } else if (tries > 50) {
        clearInterval(timer);
      }
    }, 200);
    return () => clearInterval(timer);
  }, []);

  const handleClick = () => {
    // Preferred: official LC JS API (v3).
    if (window.LiveChatWidget?.call) {
      window.LiveChatWidget.call('maximize');
      return;
    }
    // Fallback: legacy LC_API. Never throws — defensive checks only.
    if (window.LC_API?.open_chat_window) {
      window.LC_API.open_chat_window();
    }
  };

  if (!ready) return null;

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="Open chat"
      data-testid="zh-chat-fab"
      className="fixed z-[2147483646] bottom-5 right-5 md:bottom-6 md:right-6 w-14 h-14 md:w-16 md:h-16 rounded-full shadow-[0_12px_32px_rgba(0,0,0,0.22)] hover:shadow-[0_18px_44px_rgba(0,0,0,0.28)] hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 overflow-hidden bg-[#f5a623] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#f5a623]/40 flex items-center justify-center"
      style={{ WebkitTapHighlightColor: 'transparent' }}
      lang={lang}
    >
      {/*
        Intercom-style launcher icon, re-coloured for ZHELTA:
        - Shape matches Intercom's iconic rounded-square speech bubble with
          the short tail at bottom-left. Hand-traced SVG (not Intercom's
          asset) so there's no trademark issue.
        - Bubble fill = `white`. Mouth fill = the amber `#f5a623` (the
          button's own background colour) so the smile reads as if it's
          punched through the white bubble back to the button behind it.
          Using a single colour token (`currentColor` = amber via class)
          keeps the mouth auto-synced if we ever recolour the launcher.
        - Contrast: white on amber passes AAA for large shapes, and the
          amber-on-amber mouth is purely decorative so contrast isn't
          needed there — it's a visual cutout, not text.
      */}
      <svg
        viewBox="0 0 28 32"
        className="w-7 h-8 md:w-8 md:h-9 text-[#f5a623]"
        aria-hidden="true"
      >
        {/* Speech bubble body */}
        <path
          fill="white"
          d="M28 32s-4.714-1.855-8.527-3.35H3.437C1.54 28.65 0 27.112 0 25.214V3.437C0 1.54 1.54 0 3.437 0h21.125C26.46 0 28 1.54 28 3.437v18.427c0 1.898-1.54 3.437-3.437 3.437H20.16L28 32z"
        />
        {/* Smile — amber (currentColor) so it matches the button background,
            producing a "punched-through" effect. Centered inside the bubble
            at roughly 60% from the top to visually balance the bubble tail. */}
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          d="M8.5 14.6c1.1 2.2 3 3.2 5.5 3.2s4.4-1 5.5-3.2"
        />
      </svg>
    </button>
  );
}
